import React, { useEffect, useState } from "react";
import {
  Edit2,
  Eye,
  Save,
  X,
  Check,
  Building2,
  Stethoscope,
  HeartPulse,
  Activity,
  Phone,
  FileCheck,
  Sparkles,
  Loader2,
  FilePlus2,
  Printer,
} from "lucide-react";
import toast from "react-hot-toast";
import { DoctorService } from "../../../services/api/doctor.service";
import { DoctorDetailsType } from "../../../types/doctor";
import {
  ALL_VITALS_CONFIG,
  DEFAULT_VITALS_CHECKLIST,
  IDoctorPrescriptionData,
  VitalsChecklistItem,
} from "../../../types/prescription";
import PrescriptionPreviewModal from "./PrescriptionPreviewModal";

interface DoctorPrescriptionTabProps {
  doctor: DoctorDetailsType;
  doctorId: string;
}

export const DoctorPrescriptionTab: React.FC<DoctorPrescriptionTabProps> = ({
  doctor,
  doctorId,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Free-text & line split states
  const [specialisationsInput, setSpecialisationsInput] = useState<string>("");
  const [educationLine1, setEducationLine1] = useState<string>("");
  const [educationLine2, setEducationLine2] = useState<string>("");

  // Personal clinic 3 values: clinic city, clinic address, mobile
  const [personalClinicCity, setPersonalClinicCity] = useState<string>("");
  const [personalClinicAddress, setPersonalClinicAddress] = useState<string>("");
  const [personalClinicMobile, setPersonalClinicMobile] = useState<string>("");

  // Form State
  const [formData, setFormData] = useState<IDoctorPrescriptionData>({
    doctorId: doctorId || doctor?.userId || "",
    doctorName: doctor?.name || "",
    specialisations: Array.isArray(doctor?.specialization)
      ? doctor.specialization
      : [],
    education: Array.isArray(doctor?.education)
      ? doctor.education.join(", ")
      : doctor?.education || "",
    registrationNumber: doctor?.registrationNumber || "",
    vitalsChecklist: DEFAULT_VITALS_CHECKLIST,
    selfClinicAddress: "",
    selfClinicMobile: "",
  });

  // Fetch Existing Prescription Data on doctorId change
  const fetchPrescriptionData = async () => {
    if (!doctorId) return;
    try {
      setLoading(true);
      const res = await DoctorService.getDoctorPrescriptionData(doctorId);

      if (res.success && res.data) {
        const data = res.data;
        setIsConfigured(true);
        setFormData({
          id: data.id,
          doctorId: data.doctorId || doctorId,
          doctorName: doctor?.name || data.doctorName || "",
          specialisations:
            Array.isArray(data.specialisations) && data.specialisations.length > 0
              ? data.specialisations
              : Array.isArray(doctor?.specialization)
                ? doctor.specialization
                : [],
          education:
            data.education ||
            (Array.isArray(doctor?.education)
              ? doctor.education.join(", ")
              : doctor?.education || ""),
          registrationNumber:
            data.registrationNumber || doctor?.registrationNumber || "",
          vitalsChecklist:
            Array.isArray(data.vitalsChecklist) && data.vitalsChecklist.length > 0
              ? data.vitalsChecklist
              : DEFAULT_VITALS_CHECKLIST,
          selfClinicAddress: data.selfClinicAddress || "",
          selfClinicMobile: data.selfClinicMobile || "",
        });

        // Parse specialisations free-text input
        const specs =
          Array.isArray(data.specialisations) && data.specialisations.length > 0
            ? data.specialisations.join(", ")
            : Array.isArray(doctor?.specialization)
              ? doctor.specialization.join(", ")
              : (doctor?.specialization || "");
        setSpecialisationsInput(specs);

        // Parse education into Line 1 and Line 2
        const rawEdu =
          data.education ||
          (Array.isArray(doctor?.education)
            ? doctor.education.join(", ")
            : doctor?.education || "");
        const eduLines = (rawEdu || "").split(/\r?\n/);
        setEducationLine1(eduLines[0] || "");
        setEducationLine2(eduLines.slice(1).join(" ") || "");

        // Parse personal clinic: clinic city, clinic address, mobile
        let parsedCity = "";
        let parsedAddress = "";
        if (data.selfClinicAddress) {
          const dashIdx = data.selfClinicAddress.indexOf(" - ");
          if (dashIdx !== -1) {
            parsedCity = data.selfClinicAddress.substring(0, dashIdx).trim();
            parsedAddress = data.selfClinicAddress.substring(dashIdx + 3).trim();
          } else {
            parsedAddress = data.selfClinicAddress.trim();
          }
        }
        setPersonalClinicCity(parsedCity);
        setPersonalClinicAddress(parsedAddress);
        setPersonalClinicMobile(data.selfClinicMobile || "");
      } else {
        // No existing prescription found
        setIsConfigured(false);
        setIsEditing(false);
        setFormData({
          doctorId: doctorId,
          doctorName: doctor?.name || "",
          specialisations: Array.isArray(doctor?.specialization)
            ? doctor.specialization
            : [],
          education: Array.isArray(doctor?.education)
            ? doctor.education.join(", ")
            : doctor?.education || "",
          registrationNumber: doctor?.registrationNumber || "",
          vitalsChecklist: DEFAULT_VITALS_CHECKLIST,
          selfClinicAddress: "",
          selfClinicMobile: "",
        });

        const defaultSpecs = Array.isArray(doctor?.specialization)
          ? doctor.specialization.join(", ")
          : (doctor?.specialization || "");
        setSpecialisationsInput(defaultSpecs);

        const defaultEdu = Array.isArray(doctor?.education)
          ? doctor.education.join(", ")
          : (doctor?.education || "");
        const eduLines = (defaultEdu || "").split(/\r?\n/);
        setEducationLine1(eduLines[0] || "");
        setEducationLine2(eduLines.slice(1).join(" ") || "");

        setPersonalClinicCity("");
        setPersonalClinicAddress("");
        setPersonalClinicMobile("");
      }
    } catch (error) {
      console.error("Error fetching doctor prescription data:", error);
      setIsConfigured(false);
      setIsEditing(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrescriptionData();
  }, [doctorId]);

  // Vitals Checklist Toggle Handlers
  const handleToggleVital = (vitalKey: VitalsChecklistItem) => {
    const currentList = formData.vitalsChecklist || [];
    if (currentList.includes(vitalKey)) {
      setFormData({
        ...formData,
        vitalsChecklist: currentList.filter((k) => k !== vitalKey),
      });
    } else {
      setFormData({
        ...formData,
        vitalsChecklist: [...currentList, vitalKey],
      });
    }
  };

  const handleSelectAllVitals = () => {
    setFormData({
      ...formData,
      vitalsChecklist: ALL_VITALS_CONFIG.map((v) => v.key),
    });
  };

  const handleDeselectAllVitals = () => {
    setFormData({
      ...formData,
      vitalsChecklist: [],
    });
  };

  const handleResetRecommendedVitals = () => {
    setFormData({
      ...formData,
      vitalsChecklist: DEFAULT_VITALS_CHECKLIST,
    });
  };

  // Save Prescription Data
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalDoctorName = (doctor?.name || formData.doctorName || "").trim();
    if (!finalDoctorName) {
      toast.error("Doctor Name is required");
      return;
    }

    // Specialisations: parse free-text into array
    const specialisationsArray = specialisationsInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    // Education: combine Line 1 and Line 2 with \n
    const combinedEducation = [educationLine1.trim(), educationLine2.trim()]
      .filter(Boolean)
      .join("\n");

    // Personal Clinic Address: join as "clinic city - address"
    const joinedClinicAddress =
      personalClinicCity.trim() && personalClinicAddress.trim()
        ? `${personalClinicCity.trim()} - ${personalClinicAddress.trim()}`
        : personalClinicCity.trim() || personalClinicAddress.trim();

    const payload = {
      doctorId: doctorId,
      doctorName: finalDoctorName,
      specialisations: specialisationsArray,
      education: combinedEducation,
      registrationNumber: formData.registrationNumber,
      vitalsChecklist: formData.vitalsChecklist,
      selfClinicAddress: joinedClinicAddress,
      selfClinicMobile: personalClinicMobile.trim(),
    };

    try {
      setSaving(true);
      let res;
      if (isConfigured) {
        res = await DoctorService.updateDoctorPrescriptionData(doctorId, payload);
      } else {
        res = await DoctorService.createDoctorPrescriptionData(payload);
      }

      if (res && (res.success || res.data)) {
        toast.success(
          isConfigured
            ? "Prescription data updated successfully"
            : "Prescription data created successfully"
        );
        setIsConfigured(true);
        setIsEditing(false);
        if (res.data) {
          setFormData((prev) => ({
            ...prev,
            id: res.data.id || prev.id,
            ...res.data,
            doctorName: finalDoctorName,
            specialisations: specialisationsArray,
            education: combinedEducation,
            selfClinicAddress: joinedClinicAddress,
            selfClinicMobile: personalClinicMobile.trim(),
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            ...payload,
          }));
        }
      } else {
        toast.error(res?.message || "Failed to save prescription data");
      }
    } catch (error) {
      console.error("Error saving prescription data:", error);
      toast.error("An error occurred while saving prescription data");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-80 flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-[#771D51]" />
        <p className="text-sm font-medium text-gray-500">
          Fetching doctor prescription data...
        </p>
      </div>
    );
  }

  // ── CASE 1: NO PRESCRIPTION CONFIGURED YET ──
  if (!isConfigured && !isEditing) {
    return (
      <div className="w-full py-8 flex justify-center">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 sm:p-12 text-center max-w-md shadow-xs space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-[#771D51]/10 text-[#771D51] flex items-center justify-center mx-auto shadow-xs">
            <FilePlus2 className="w-8 h-8" />
          </div>

          <div className="space-y-1">
            <h3 className="text-lg font-bold text-gray-900">
              No Prescription Configured
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
              No prescription configuration or vitals preferences were found for this doctor. Click below to add and configure prescription data.
            </p>
          </div>

          <div className="pt-2">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#771D51] hover:bg-[#5E1640] transition-all shadow-sm cursor-pointer"
            >
              <span>+ Add New Prescription</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── CASE 2: EDITING / CREATING MODE ──
  if (isEditing) {
    return (
      <div className="w-full space-y-5 pb-6">
        {/* Form Header Bar */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-gray-900">
              {isConfigured
                ? "Edit Doctor Prescription Data"
                : "Add New Doctor Prescription"}
            </h2>
            <p className="text-xs text-gray-500">
              Fill in the prescription header metadata, vitals checklist, and clinic addresses.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                fetchPrescriptionData();
              }}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
              <span>Cancel</span>
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#199C4D] hover:bg-[#15803d] transition-all shadow-xs disabled:opacity-60 cursor-pointer"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              <span>{saving ? "Saving..." : isConfigured ? "Update Prescription" : "Save Prescription"}</span>
            </button>
          </div>
        </div>

        {/* Edit Form */}
        <form
          onSubmit={handleSave}
          className="bg-white border border-gray-200 rounded-xl p-5 shadow-xs space-y-5 max-w-3xl"
        >
          {/* Section 1: Prescription Metadata */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                <Stethoscope className="w-4 h-4 text-[#771D51]" />
                Doctor Header Details
              </h3>
              <span className="text-[10px] text-gray-400 font-medium">
                Fields printed in prescription header
              </span>
            </div>

            {/* Doctor Name - Read-only / cannot be changed */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-gray-700">
                  Doctor Full Name <span className="text-red-500">*</span>
                </label>
                <span className="text-[10px] text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded">
                  Locked (From Doctor Profile)
                </span>
              </div>
              <input
                type="text"
                value={doctor?.name || formData.doctorName}
                readOnly
                disabled
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none"
              />
            </div>

            {/* Specialisations - Free Text Input */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Specialisations
              </label>
              <input
                type="text"
                value={specialisationsInput}
                onChange={(e) => setSpecialisationsInput(e.target.value)}
                placeholder="e.g. General Physician, Cardiologist"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#771D51]/30 focus:border-[#771D51]"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                Enter specializations separated by comma (e.g. General Physician, Cardiologist)
              </p>
            </div>

            {/* Qualifications / Education (2 Lines) */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-gray-700">
                Qualifications / Education (2 Lines)
              </label>
              <div>
                <input
                  type="text"
                  value={educationLine1}
                  onChange={(e) => setEducationLine1(e.target.value)}
                  placeholder="Line 1: e.g. MBBS, MD (General Medicine)"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#771D51]/30 focus:border-[#771D51]"
                />
              </div>
              <div>
                <input
                  type="text"
                  value={educationLine2}
                  onChange={(e) => setEducationLine2(e.target.value)}
                  placeholder="Line 2: e.g. Fellowship in Cardiology, PGDHM (Optional)"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#771D51]/30 focus:border-[#771D51]"
                />
              </div>
              <p className="text-[10px] text-gray-400">
                Line 1 and Line 2 will be separated by a line break on the prescription pad.
              </p>
            </div>

            {/* Medical Registration Number */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Medical Registration / License Number
              </label>
              <input
                type="text"
                value={formData.registrationNumber}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    registrationNumber: e.target.value,
                  })
                }
                placeholder="e.g. 23568962158976"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#771D51]/30 focus:border-[#771D51]"
              />
            </div>
          </div>

          {/* Section 2: Vitals Checklist */}
          <div className="pt-3 border-t border-gray-100 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Activity className="w-4 h-4 text-[#771D51]" />
                  Vitals Checklist Preferences
                </label>
                <p className="text-[10px] text-gray-400">
                  Select which vitals to capture & print on prescriptions.
                </p>
              </div>

              {/* Quick selectors */}
              <div className="flex items-center gap-1 text-[11px] font-semibold">
                <button
                  type="button"
                  onClick={handleSelectAllVitals}
                  className="px-2 py-0.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                >
                  All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={handleResetRecommendedVitals}
                  className="px-2 py-0.5 text-[#771D51] hover:bg-[#771D51]/10 rounded flex items-center gap-1"
                >
                  <Sparkles className="w-3 h-3" />
                  Defaults
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={handleDeselectAllVitals}
                  className="px-2 py-0.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ALL_VITALS_CONFIG.map((vital) => {
                const isChecked = (formData.vitalsChecklist || []).includes(
                  vital.key
                );
                return (
                  <label
                    key={vital.key}
                    onClick={() => handleToggleVital(vital.key)}
                    className={`flex items-start gap-2 p-2 rounded-lg border text-xs cursor-pointer transition-all ${isChecked
                        ? "bg-[#771D51]/5 border-[#771D51]/30 text-gray-900"
                        : "bg-gray-50/70 border-gray-200 text-gray-600 hover:bg-gray-100/60"
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => { }}
                      className="mt-0.5 rounded text-[#771D51] focus:ring-[#771D51]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900 text-xs">
                          {vital.label}
                        </span>
                        {vital.unit && (
                          <span className="text-[10px] text-gray-400 font-mono">
                            ({vital.unit})
                          </span>
                        )}
                      </div>
                      {vital.description && (
                        <p className="text-[10px] text-gray-400 truncate mt-0.5">
                          {vital.description}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Section 3: Clinic Addresses (2 Addresses) */}
          <div className="pt-3 border-t border-gray-100 space-y-3">
            <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-[#771D51]" />
              Prescription Clinic Addresses (2 Addresses)
            </h4>

            {/* Address 1: Current City Clinic */}
            <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="font-bold text-gray-700">
                  1. Current City Clinic / Hospital
                </span>
                <span className="text-[10px] px-1.5 py-0.2 bg-gray-200 text-gray-600 rounded">
                  Linked Profile Address
                </span>
              </div>
              <p className="text-gray-600">
                <span className="font-semibold text-gray-800">
                  {doctor?.city || "Current City"} -:{" "}
                </span>
                {[
                  doctor?.hospitalName,
                  doctor?.address,
                  doctor?.city,
                  doctor?.pincode,
                  doctor?.state,
                ]
                  .filter(Boolean)
                  .join(", ") || "No clinic address registered"}
              </p>
            </div>

            {/* Address 2: Doctor's Personal Clinic */}
            <div className="space-y-2.5 p-3 bg-emerald-50/50 border border-emerald-200/60 rounded-lg text-xs">
              <div className="flex items-center justify-between">
                <span className="font-bold text-emerald-900">
                  2. Doctor's Personal Clinic Address
                </span>
                <span className="text-[10px] px-1.5 py-0.2 bg-emerald-100 text-emerald-800 rounded font-medium">
                  Custom / Optional
                </span>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                  Clinic City
                </label>
                <input
                  type="text"
                  value={personalClinicCity}
                  onChange={(e) => setPersonalClinicCity(e.target.value)}
                  placeholder="e.g. Lucknow"
                  className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#199C4D]/30 focus:border-[#199C4D]"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                  Clinic Address
                </label>
                <textarea
                  rows={2}
                  value={personalClinicAddress}
                  onChange={(e) => setPersonalClinicAddress(e.target.value)}
                  placeholder="e.g. B-1/298, Sahara States Rd, Jankipuram"
                  className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#199C4D]/30 focus:border-[#199C4D]"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                  Personal Clinic Contact Mobile
                </label>
                <div className="relative">
                  <Phone className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2" />
                  <input
                    type="text"
                    value={personalClinicMobile}
                    onChange={(e) => setPersonalClinicMobile(e.target.value)}
                    placeholder="e.g. 9621837303"
                    className="w-full pl-8 pr-2.5 py-1.5 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#199C4D]/30 focus:border-[#199C4D]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                fetchPrescriptionData();
              }}
              disabled={saving}
              className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 px-5 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#771D51] hover:bg-[#5E1640] transition-all shadow-xs disabled:opacity-60 cursor-pointer"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              <span>
                {saving
                  ? "Saving..."
                  : isConfigured
                    ? "Update Prescription"
                    : "Save Prescription"}
              </span>
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── CASE 3: PRESCRIPTION EXISTS - CLEAN SUMMARY CARDS VIEW ──
  return (
    <div className="w-full space-y-5 pb-6">
      {/* View Header Bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-base font-bold text-gray-900">
              Doctor Prescription Data
            </h2>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <FileCheck className="w-3 h-3" />
              Configured
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Prescription header metadata, active vitals checklist, and clinic addresses.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-[#771D51] bg-[#771D51]/10 hover:bg-[#771D51]/20 transition-all cursor-pointer"
          >
            <Edit2 className="w-3.5 h-3.5" />
            <span>Edit Prescription</span>
          </button>

          <button
            type="button"
            onClick={() => setShowPreviewModal(true)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer border border-gray-200"
          >
            <Eye className="w-3.5 h-3.5 text-gray-600" />
            <span>Preview</span>
          </button>
        </div>
      </div>

      {/* View Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Card 1: Doctor Header Details */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-xs space-y-3">
          <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#771D51]/10 flex items-center justify-center text-[#771D51]">
                <Stethoscope className="w-3.5 h-3.5" />
              </div>
              <h3 className="text-xs font-bold text-gray-900">
                Doctor Header Info
              </h3>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs font-semibold text-[#771D51] hover:underline flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" />
              Edit
            </button>
          </div>

          <div className="space-y-2.5 text-xs">
            <div>
              <span className="text-gray-400 block font-medium">
                Doctor Name:
              </span>
              <span className="font-bold text-[#199C4D] text-sm">
                {formData.doctorName || "Dr. Name"}
              </span>
            </div>

            <div>
              <span className="text-gray-400 block font-medium">
                Medical Reg. No:
              </span>
              <span className="font-semibold text-gray-800">
                {formData.registrationNumber || "Not Provided"}
              </span>
            </div>

            <div>
              <span className="text-gray-400 block font-medium">
                Qualifications / Education:
              </span>
              <span className="font-medium text-gray-800 leading-snug block whitespace-pre-line">
                {formData.education || "MBBS, MD"}
              </span>
            </div>

            <div>
              <span className="text-gray-400 block font-medium mb-1">
                Specialisations:
              </span>
              <div className="flex flex-wrap gap-1">
                {Array.isArray(formData.specialisations) && formData.specialisations.length > 0 ? (
                  formData.specialisations.map((spec, index) => (
                    <span
                      key={index}
                      className="px-2 py-0.5 rounded-md bg-[#A33474]/10 text-[#A33474] font-semibold text-[10px]"
                    >
                      {spec}
                    </span>
                  ))
                ) : typeof formData.specialisations === "string" && formData.specialisations ? (
                  <span className="px-2 py-0.5 rounded-md bg-[#A33474]/10 text-[#A33474] font-semibold text-[10px]">
                    {formData.specialisations}
                  </span>
                ) : (
                  <span className="text-gray-400 italic">None specified</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Active Vitals Checklist */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-xs space-y-3">
          <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center text-[#199C4D]">
                <HeartPulse className="w-3.5 h-3.5" />
              </div>
              <h3 className="text-xs font-bold text-gray-900">
                Active Vitals Checklist
              </h3>
            </div>
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
              {formData.vitalsChecklist?.length || 0} active
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5 pt-0.5 max-h-52 overflow-y-auto">
            {formData.vitalsChecklist && formData.vitalsChecklist.length > 0 ? (
              formData.vitalsChecklist.map((vitalKey) => {
                const item = ALL_VITALS_CONFIG.find((v) => v.key === vitalKey);
                return (
                  <span
                    key={vitalKey}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-[11px] font-medium text-gray-800"
                  >
                    <Check className="w-3 h-3 text-[#199C4D]" />
                    {item?.shortLabel || vitalKey}
                  </span>
                );
              })
            ) : (
              <p className="text-xs text-gray-400 italic">
                No vitals selected. Click edit to choose required vitals.
              </p>
            )}
          </div>
        </div>

        {/* Card 3: Clinic Addresses (2 Addresses) */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-xs space-y-3">
          <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                <Building2 className="w-3.5 h-3.5" />
              </div>
              <h3 className="text-xs font-bold text-gray-900">
                Clinic Addresses (2)
              </h3>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs font-semibold text-[#771D51] hover:underline flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" />
              Edit
            </button>
          </div>

          <div className="space-y-2.5 text-xs text-gray-700">
            <div className="p-2.5 bg-gray-50 rounded-lg">
              <span className="font-bold text-gray-900 block mb-0.5">
                1. Current City Clinic:
              </span>
              <span>
                {doctor?.city || "Current City"} -:{" "}
                {[
                  doctor?.hospitalName,
                  doctor?.address,
                  doctor?.city,
                  doctor?.pincode,
                  doctor?.state,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </div>

            <div className="p-2.5 bg-emerald-50/60 border border-emerald-100 rounded-lg">
              <span className="font-bold text-emerald-900 block mb-0.5">
                2. Personal Clinic:
              </span>
              <span>{formData.selfClinicAddress || "Not configured"}</span>
              {formData.selfClinicMobile && (
                <span className="block text-emerald-700 font-semibold mt-1">
                  📞 {formData.selfClinicMobile}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview & Print Modal */}
      <PrescriptionPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        prescriptionData={formData}
        defaultDoctorData={{
          name: doctor?.name,
          education: doctor?.education,
          specialization: doctor?.specialization,
          registrationNumber: doctor?.registrationNumber,
          hospitalName: doctor?.hospitalName,
          address: doctor?.address,
          city: doctor?.city,
          state: doctor?.state,
          pincode: doctor?.pincode,
        }}
      />
    </div>
  );
};

export default DoctorPrescriptionTab;
